import { TEMPLATE_SITE_TYPE } from '@/components/SiteOptions/types';
import { api } from '@/data/api';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import { DreamValue } from '@/sharedComponents/TextEditor/plate';
import { useGetSpaceQuery } from '@/store';
import {
  BlogPageTemplateType,
  IPageCustomization,
  PageTemplateType,
  SectionTemplateType,
} from '@/types';
import { errToString } from '@/utils/errors';
import TokenContext from '@/utils/jwt/tokenContext';
import { ITemplate, TEMPLATE_TYPE, validUUID } from '@typedream/data';
import { AxiosResponse } from 'axios';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SAVE_TEMPLATE_PAGE } from '../types';
import { SaveTemplatePopupContext } from './context';

export function SavePageTemplatePopupProvider({ children }: { children: ReactNode }) {
  const [open, setopen] = useState(false);
  const [step, setstep] = useState(SAVE_TEMPLATE_PAGE.CREATE);
  const [content, setcontent] = useState<DreamValue>(null);
  const [customization, setcustomization] = useState<IPageCustomization>(null);
  const [err, seterr] = useState(null);
  const [spaceID] = useSpaceAndPageIDFromRouter();
  const [pageID, setPageID] = useState<string>(null);
  const [loadingTemplates, setloadingTemplates] = useState(false);
  const [templates, settemplates] = useState(undefined);
  const [templateToUpdate, settemplateToUpdate] = useState(undefined);

  const { data: space } = useGetSpaceQuery([spaceID], { skip: !spaceID });

  const { spacePermissionTokenHandler, subscriptionTokenHandler } = useContext(TokenContext);

  const getTemplates = useCallback(async () => {
    setloadingTemplates(true);
    try {
      const resp = await api().template().list(spaceID, true, spacePermissionTokenHandler);
      settemplates(
        resp?.data?.filter(
          (template: BlogPageTemplateType | PageTemplateType | SectionTemplateType) =>
            template.type === 'page' || template.type === 'blog_page' // both of these are page templates
        )
      );
    } catch (e) {
      seterr(errToString(e));
    }
    setloadingTemplates(false);
  }, [spaceID, spacePermissionTokenHandler]);

  useEffect(() => {
    if (open && spaceID) {
      getTemplates();
    }
  }, [spaceID, getTemplates, open]);

  useEffect(() => {
    if (open && templates !== undefined) {
      setstep(
        templates?.length > 0 ? SAVE_TEMPLATE_PAGE.EXISTING_OR_NEW : SAVE_TEMPLATE_PAGE.CREATE
      );
    }
  }, [templates, open]);

  const categoryOptions = Object.values(TEMPLATE_SITE_TYPE);

  const onCreateTemplate = useCallback(
    async ({
      name,
      description,
      category,
      previewImageFile,
      pid,
    }: {
      name: string;
      description: string;
      category: string[];
      previewImageFile: File;
      pid?: string;
    }): Promise<ITemplate<TEMPLATE_TYPE.PAGE>> => {
      let newTemplate: Partial<ITemplate<TEMPLATE_TYPE.PAGE>> = {
        name,
        description,
        category,
        content,
        published_content: content,
        space_id: spaceID,
        space,
        type: TEMPLATE_TYPE.PAGE,
        customization,
      };

      if (validUUID(pid)) {
        newTemplate = {
          ...newTemplate,
          metadata: {
            onboarding: false,
            style: [],
            usecase: [],
            sections: [],
            page_id: pid,
            pages: [],
            price: 0,
            price_currency: 'usd',
            checkout_url: '',
            thumbnail: '',
            screenshots: [],
            categories: [],
            profile_name: '',
            profile_avatar: '',
            profile_url: '',
            long_description: '',
          },
        };
      }

      try {
        if (previewImageFile) {
          const payload = await api()
            .document()
            .upload(
              previewImageFile,
              spaceID,
              spacePermissionTokenHandler,
              subscriptionTokenHandler
            );
          const filename = payload.data.file_name;
          const imgURL = api().document().getURL(filename);
          newTemplate.preview = imgURL;
        }

        const res: AxiosResponse<ITemplate<TEMPLATE_TYPE.PAGE>> = await api()
          .template()
          .create(
            spaceID,
            newTemplate as ITemplate<TEMPLATE_TYPE.PAGE>,
            spacePermissionTokenHandler
          );

        return res.data;
      } catch (e) {
        seterr(errToString(e));
        return null;
      }
    },
    [
      content,
      customization?.collection_data,
      spaceID,
      spacePermissionTokenHandler,
      subscriptionTokenHandler,
    ]
  );

  const onUpdateTemplate = useCallback(async () => {
    seterr(null);

    const updatedTemplate: Partial<ITemplate<TEMPLATE_TYPE.PAGE>> = {
      ...templateToUpdate,
      content,
      published_content: content,
      customization,
      space_id: spaceID,
    };

    try {
      await api()
        .template()
        .update(
          spaceID,
          templateToUpdate.id,
          updatedTemplate as ITemplate<TEMPLATE_TYPE.PAGE>,
          spacePermissionTokenHandler
        );
      setstep(SAVE_TEMPLATE_PAGE.SUCCESS);
    } catch (e) {
      seterr(errToString(e));
    }
  }, [
    content,
    customization?.collection_data,
    spaceID,
    spacePermissionTokenHandler,
    templateToUpdate,
  ]);
  return (
    <SaveTemplatePopupContext.Provider
      value={useMemo(
        () => ({
          open,
          setopen,
          step,
          setstep,
          content,
          setcontent,
          customization,
          setcustomization,
          err,
          seterr,
          templateType: TEMPLATE_TYPE.PAGE,
          templates,
          settemplates,
          templateToUpdate,
          settemplateToUpdate,
          loadingTemplates,
          categoryOptions,
          onCreateTemplate,
          onUpdateTemplate,
          pageID,
          setPageID,
        }),
        [
          categoryOptions,
          err,
          loadingTemplates,
          onCreateTemplate,
          onUpdateTemplate,
          open,
          content,
          customization,
          step,
          templateToUpdate,
          templates,
          pageID,
        ]
      )}
    >
      {children}
    </SaveTemplatePopupContext.Provider>
  );
}
